import { useState, useEffect } from 'react';
import { useLang } from '../context/LangContext';
import { UI } from '../i18n/translations';
import '../css/navbar.css';

export default function Navbar() {
  const [open, setOpen]       = useState(false);
  const [active, setActive]   = useState('home');
  const [dark, setDark]       = useState(() => localStorage.getItem('amko-theme') === 'dark');
  const [langHint, setLangHint] = useState(() => {
    const isMobile = window.matchMedia('(hover: none) and (pointer: coarse)').matches
                     || window.innerWidth < 1024;
    return isMobile && !localStorage.getItem('amko_lang_hint_seen');
  });
  const { lang, setLang } = useLang();
  const t = UI[lang];

  const close = () => setOpen(false);

  const openMenu = () => {
    setOpen(o => !o);
    if (langHint) {
      setLangHint(false);
      localStorage.setItem('amko_lang_hint_seen', 'true');
    }
  };

  const links = [
    { id: 'home',      label: t.navHome },
    { id: 'about',     label: t.navAbout },
    { id: 'services',  label: t.navServices },
    { id: 'portfolio', label: t.navPortfolio },
    { id: 'reviews',   label: t.navReviews },
    { id: 'contact',   label: t.navContact },
  ];

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    localStorage.setItem('amko-theme', dark ? 'dark' : 'light');
  }, [dark]);

  const NAV_IDS = ['home', 'about', 'services', 'portfolio', 'reviews', 'contact'];
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); }),
      { threshold: 0.3 }
    );
    NAV_IDS.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollTo = id => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 64;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    close();
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-toggle-wrap">
          <button className="navbar-toggle" onClick={openMenu} aria-label="Menu">☰</button>
          {langHint && (
            <div className="lang-hint-bubble">
              🌐 Tap to change language
              <span className="lang-hint-arrow" />
            </div>
          )}
        </div>
        <button className="navbar-logo" onClick={() => scrollTo('home')}>
          <img src="/logo.png" alt="AMKO Logo" className="navbar-logo-img" />
          AMKO<span className="navbar-logo-accent">IQ</span>
        </button>
        <div className="navbar-controls">
          {/* Language toggle — hidden on mobile, shown in drawer instead */}
          <div className="lang-toggle lang-toggle-desktop" role="group" aria-label="Language">
            <button className={`lang-btn ${lang === 'en' ? 'active' : ''}`} onClick={() => setLang('en')} aria-pressed={lang === 'en'}>EN</button>
            <button className={`lang-btn ${lang === 'ar' ? 'active' : ''}`} onClick={() => setLang('ar')} aria-pressed={lang === 'ar'}>AR</button>
            <button className={`lang-btn ${lang === 'ku' ? 'active' : ''}`} onClick={() => setLang('ku')} aria-pressed={lang === 'ku'}>KU</button>
          </div>
          <button className="theme-toggle" onClick={() => setDark(d => !d)} aria-label="Toggle theme">
            {dark ? '☀' : '🌙'}
          </button>
        </div>
      </nav>

      {open && <div className="drawer-overlay" onClick={close} />}

      <div className={`drawer ${open ? 'drawer-open' : ''}`}>
        <button className="drawer-close" onClick={close}>✕</button>
        <ul className="drawer-links">
          {links.map(l => (
            <li key={l.id}>
              <button className={active === l.id ? 'active' : ''} onClick={() => scrollTo(l.id)}>
                {l.label}
              </button>
            </li>
          ))}
          <li>
            <button className="drawer-btn" onClick={() => scrollTo('contact')}>
              {t.navBook}
            </button>
          </li>
        </ul>

        {/* Language switcher inside drawer — only visible on mobile */}
        <div className="drawer-lang">
          <span className="drawer-lang-label">Language</span>
          <div className="drawer-lang-btns">
            {[['en','EN — English'],['ar','AR — العربية'],['ku','KU — کوردی']].map(([code, label]) => (
              <button key={code}
                className={`drawer-lang-btn ${lang === code ? 'active' : ''}`}
                onClick={() => { setLang(code); close(); }}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
